import type {
  AdminUserListResponse,
  OrganizationDetail,
  OrganizationFrame,
  OrganizationSummary,
  OrganizationUpdatePayload,
  OrganizationUser,
  OrganizationUserCreatePayload,
  OrganizationUserUpdatePayload,
} from "@kontextwerk/control-web-client"
import { getAdminApi } from "../client"

export type OrganizationAccessRef = {
  id: string
  name: string
  slug: string
  status: string
  tier: string
  is_admin_org?: boolean
}

export type OrganizationListQuery = {
  query?: string | null
  offset?: number | null
  limit?: number | null
}

export type OrganizationListResponse = {
  items: OrganizationSummary[]
  total: number
}

export type OrganizationUserListQuery = {
  query?: string | null
  offset?: number | null
  limit?: number | null
}

const cacheTtlMs = 5 * 60 * 1000
type CacheEntry<T> = { value: T; expiresAt: number }

const organizationAccessRefCache = new Map<string, CacheEntry<OrganizationAccessRef>>()
const organizationAccessRefInflight = new Map<string, Promise<OrganizationAccessRef>>()
const organizationUsersCache = new Map<string, CacheEntry<AdminUserListResponse>>()
const organizationUsersInflight = new Map<string, Promise<AdminUserListResponse>>()

const readCache = <T>(cache: Map<string, CacheEntry<T>>, key: string): T | null => {
  const entry = cache.get(key)

  if (!entry) return null

  if (entry.expiresAt < Date.now()) {
    cache.delete(key)

    return null
  }

  return entry.value
}

const writeCache = <T>(cache: Map<string, CacheEntry<T>>, key: string, value: T) => {
  cache.set(key, { value, expiresAt: Date.now() + cacheTtlMs })
}

const usersCacheKey = (orgId: string, query?: OrganizationUserListQuery) =>
  JSON.stringify({
    orgId,
    query: query?.query ?? null,
    offset: query?.offset ?? null,
    limit: query?.limit ?? null,
  })

export const fetchOrganizationsWithMeta = async (
  query?: OrganizationListQuery,
): Promise<OrganizationListResponse> => {
  const response = await getAdminApi().raw.request<
    OrganizationSummary[] | OrganizationListResponse
  >("GET", "/organization-registry", {
    params: {
      query: query?.query ?? undefined,
      q: query?.query ?? undefined,
      offset: query?.offset ?? undefined,
      limit: query?.limit ?? undefined,
    },
  })

  if (Array.isArray(response)) {
    return { items: response, total: response.length }
  }

  const items = response.items ?? []

  return { items, total: response.total ?? items.length }
}

export const resetOrganizationProjects = (orgId: string) =>
  getAdminApi().organization.provisioning.resetProjects(orgId)

export const fetchOrganization = (orgId: string): Promise<OrganizationDetail> =>
  getAdminApi().organization.registry.get(orgId)

export const fetchOrganizationAccessRef = (orgId: string): Promise<OrganizationAccessRef> => {
  const cached = readCache(organizationAccessRefCache, orgId)

  if (cached) return Promise.resolve(cached)

  const inflight = organizationAccessRefInflight.get(orgId)

  if (inflight) return inflight

  const request = getAdminApi()
    .raw.request<OrganizationAccessRef>(
      "GET",
      `/organizations/${encodeURIComponent(orgId)}/access-ref`,
    )
    .then((response) => {
      writeCache(organizationAccessRefCache, orgId, response)
      writeCache(organizationAccessRefCache, response.id, response)
      writeCache(organizationAccessRefCache, response.slug, response)

      return response
    })
    .finally(() => {
      organizationAccessRefInflight.delete(orgId)
    })
  organizationAccessRefInflight.set(orgId, request)

  return request
}

export const fetchOrganizationFrame = (orgId: string): Promise<OrganizationFrame> =>
  getAdminApi().organization.registry.getFrame(orgId)

export const updateOrganization = (orgId: string, payload: OrganizationUpdatePayload) =>
  getAdminApi()
    .organization.registry.update(orgId, payload)
    .then((response) => {
      invalidateOrganizationCaches(orgId)
      invalidateOrganizationCaches(response?.id)

      return response
    })

export const fetchOrganizationUsers = (
  orgId: string,
  query?: OrganizationUserListQuery,
): Promise<AdminUserListResponse> =>
  getAdminApi().users.listForOrg(orgId, {
    query: query?.query ?? undefined,
    limit: query?.limit ?? undefined,
    offset: query?.offset ?? undefined,
  })

export const fetchOrganizationUsersCached = (
  orgId: string,
  query?: OrganizationUserListQuery,
): Promise<AdminUserListResponse> => {
  const key = usersCacheKey(orgId, query)
  const cached = readCache(organizationUsersCache, key)

  if (cached) return Promise.resolve(cached)

  const inflight = organizationUsersInflight.get(key)

  if (inflight) return inflight

  const request = fetchOrganizationUsers(orgId, query)
    .then((response) => {
      writeCache(organizationUsersCache, key, response)

      return response
    })
    .finally(() => {
      organizationUsersInflight.delete(key)
    })
  organizationUsersInflight.set(key, request)

  return request
}

export const invalidateOrganizationCaches = (orgId?: string | null) => {
  if (!orgId) {
    organizationAccessRefCache.clear()
    organizationUsersCache.clear()

    return
  }

  organizationAccessRefCache.delete(orgId)
  for (const key of organizationUsersCache.keys()) {
    if (key.includes(`"orgId":"${orgId}"`)) organizationUsersCache.delete(key)
  }
}

export const createOrganizationUser = (
  orgId: string,
  payload: OrganizationUserCreatePayload,
): Promise<OrganizationUser> =>
  getAdminApi()
    .users.createForOrg(orgId, payload)
    .then((response) => {
      invalidateOrganizationCaches(orgId)

      return response
    })

export const updateOrganizationUser = (
  orgId: string,
  userId: string,
  payload: OrganizationUserUpdatePayload,
): Promise<OrganizationUser> =>
  getAdminApi()
    .users.updateForOrg(orgId, userId, payload)
    .then((response) => {
      invalidateOrganizationCaches(orgId)

      return response
    })

export const deleteOrganizationUser = (orgId: string, userId: string): Promise<void> =>
  getAdminApi()
    .users.deleteForOrg(orgId, userId)
    .then((response) => {
      invalidateOrganizationCaches(orgId)

      return response
    })
